import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormGroup,
  FormControl,
  FormArray,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  ExperienceEntry,
  Experiences,
} from 'src/app/shared/models/caregiver.model';

// ── Types ────────────────────────────────────────────────────────────────────

interface FlatEntry extends ExperienceEntry {
  clusterKey: ClusterKey;
  ageRangeLabel: string;
}

type ClusterKey = 'cluster1' | 'cluster2' | 'cluster3' | 'cluster4';

// ── Constants ────────────────────────────────────────────────────────────────

const CLUSTER_META: Record<ClusterKey, { label: string; ageRange: string }> = {
  cluster1: { label: '0–6 mesi', ageRange: '0–6 mesi' },
  cluster2: { label: '7–24 mesi', ageRange: '7–24 mesi' },
  cluster3: { label: '2–5 anni', ageRange: '2–5 anni' },
  cluster4: { label: '5+ anni', ageRange: '5+ anni' },
};

const TASK_LABELS: Record<string, string> = {
  hygiene: 'Cambio pannolini e igiene del bambino',
  sleep: 'Accompagnamento alla nanna / al sonno',
  food: 'Preparazione pappa',
  weaning: 'Svezzamento',
  foodSafety: 'Tagli sicuri degli alimenti e autosvezzamento',
  cry: 'Gestione del pianto',
  crawling: 'Stimolo al gattonamento',
  montessori: 'Approccio al gioco Montessori',
  language: 'Attestato di sviluppo del linguaggio',
  motory: 'Stimolo allo sviluppo motorio',
  illness: 'Gestione della malattia',
  motoryActivities: "Attività motoria all'aperto",
  emotionManagement: 'Gestione emotiva con altri bambini',
  spannolinamento: 'Avvio dello spannolinamento',
  creativeGames: 'Gioco creativo',
  homeworks: 'Portare a termine i compiti',
  freeTime: 'Gestione del tempo libero',
  extra: 'Accompagnamento attività extrascolastiche',
  social: 'Socializzazione con altri animali',
  muzzle: 'Abituazione alla museruola',
  interactions: 'Gestione delle interazioni',
  walking: 'Passeggiate al guinzaglio',
  helpers: 'Uso di ausili / carrozzine',
  diseases: 'Gestione di patologie croniche',
  assistance: 'Assistenza alla persona',
  care: 'Cura e igiene personale',
  drugs: 'Somministrazione farmaci',
  cooking: 'Preparazione pasti',
  other: 'Altro',
};

const TASKS_BY_CLUSTER: Record<string, Record<number, string[]>> = {
  babysitter: {
    1: ['hygiene', 'sleep', 'food', 'weaning', 'foodSafety', 'other'],
    2: [
      'cry',
      'crawling',
      'montessori',
      'language',
      'foodSafety',
      'motory',
      'other',
    ],
    3: [
      'sleep',
      'illness',
      'motoryActivities',
      'emotionManagement',
      'spannolinamento',
      'creativeGames',
      'other',
    ],
    4: ['homeworks', 'freeTime', 'extra', 'other'],
  },
  petsitter: {
    1: ['social', 'muzzle', 'other'],
    2: ['interactions', 'walking', 'other'],
    3: ['helpers', 'diseases', 'other'],
  },
  nurse: { 1: ['assistance', 'care', 'drugs', 'cooking', 'other'] },
  colf: { 1: ['other'] },
};

const EMPLOYER_OPTIONS: Record<string, string[]> = {
  petsitter: ['Famiglia privata', 'Struttura', 'Altro'],
  nurse: ['Casa di cura/RSA', 'Famiglia privata', 'Altro'],
  colf: ['Famiglia privata', 'Ditte o Strutture', 'Altro'],
};

const AGE_OPTIONS: Record<string, string[]> = {
  babysitter: ['0-6 Mesi', '7-24 Mesi', '2-5 Anni', '5+ Anni'],
  petsitter: ['Cuccioli', 'Adulti', 'Anziani'],
};

const MONTHS = [
  'Gennaio',
  'Febbraio',
  'Marzo',
  'Aprile',
  'Maggio',
  'Giugno',
  'Luglio',
  'Agosto',
  'Settembre',
  'Ottobre',
  'Novembre',
  'Dicembre',
];

// ── Component ────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-caregiver-experience-chronological',
  standalone: true,
  templateUrl: './caregiver-experience.component.html',
  styleUrls: ['./caregiver-experience.component.scss'],
  imports: [CommonModule, ReactiveFormsModule],
})
export class CaregiverExperienceChronologicalComponent
  implements OnChanges, OnInit
{
  @Input() experiences: Experiences | null = null;
  @Input() type: 'babysitter' | 'petsitter' | 'colf' | 'nurse' = 'babysitter';

  @Output() saveExperienceItem = new EventEmitter<{
    experiences: Experiences;
  }>();
  @Output() deleteExperienceItem = new EventEmitter<{
    clusterKey: ClusterKey;
    clusterIndex: number;
  }>();

  // ── Timeline ─────────────────────────────────────────────────────────────
  timeline: FlatEntry[] = [];

  // ── Modal state ──────────────────────────────────────────────────────────
  showModal = false;
  editMode = false;
  editingEntry: (FlatEntry & { clusterIndex: number }) | null = null;

  // ── Form ─────────────────────────────────────────────────────────────────
  form!: FormGroup;
  readonly months = MONTHS;
  readonly currentYear = new Date().getFullYear();
  readonly maxExtra = 5;

  // ── Derived getters ───────────────────────────────────────────────────────
  get showAgeSelect(): boolean {
    return this.type === 'babysitter' || this.type === 'petsitter';
  }

  get ageOptions(): string[] {
    return AGE_OPTIONS[this.type] ?? [];
  }

  get employerOptions(): string[] {
    if (this.type === 'babysitter') {
      const cluster = +this.form?.get('clusterNumber')?.value;
      // clusters 1 (0-6m) and 2 (7-24m) — no school options
      if (cluster === 1 || cluster === 2) {
        return ['Famiglia privata', 'Asilo', 'Altro'];
      }
      // clusters 3 (2-5y) and 4 (5+y) — include schools
      return [
        'Famiglia privata',
        'Asilo',
        'Scuola primaria',
        'Scuola secondaria',
        'Altro',
      ];
    }
    return EMPLOYER_OPTIONS[this.type] ?? [];
  }

  get tasksGroup(): FormGroup {
    return this.form.get('tasks') as FormGroup;
  }
  get extraActivities(): FormArray {
    return this.form.get('extraActivities') as FormArray;
  }
  get isCurrently(): boolean {
    return !!this.form?.get('currentlyWorking')?.value;
  }

  get availableTasks(): string[] {
    const cluster = +this.form?.get('clusterNumber')?.value;
    return cluster ? TASKS_BY_CLUSTER[this.type]?.[cluster] ?? [] : [];
  }

  get dateError(): string {
    if (this.isCurrently) return '';
    const sy = +this.form.get('startYear')?.value;
    const sm = +this.form.get('startMonth')?.value;
    const ey = +this.form.get('endYear')?.value;
    const em = +this.form.get('endMonth')?.value;
    if (sy && sm && ey && em && new Date(ey, em - 1) <= new Date(sy, sm - 1))
      return 'La data di fine deve essere successiva alla data di inizio.';
    return '';
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['experiences']) {
      this.buildTimeline();
    }
  }

  // ── Timeline builder ──────────────────────────────────────────────────────

  private buildTimeline(): void {
    if (!this.experiences) {
      this.timeline = [];
      return;
    }

    const flat: FlatEntry[] = [];

    (Object.keys(CLUSTER_META) as ClusterKey[]).forEach((key) => {
      const entries = this.experiences![key] ?? [];
      entries
        .filter((e) => e.employer !== 'NOT_EXPERIENCED')
        .forEach((e) => {
          flat.push({
            ...e,
            clusterKey: key,
            ageRangeLabel: this.formatAge(CLUSTER_META[key].ageRange),
          });
        });
    });

    this.timeline = [...flat].sort((a, b) => {
      if (!a.endMonth && b.endMonth) return -1;
      if (a.endMonth && !b.endMonth) return 1;
      if (!a.startMonth && !b.startMonth) return 0;
      if (!a.startMonth) return 1;
      if (!b.startMonth) return -1;
      return b.startMonth.localeCompare(a.startMonth);
    });
  }

  // ── Form init ─────────────────────────────────────────────────────────────

  private initForm(): void {
    this.form = new FormGroup({
      clusterNumber: new FormControl(
        this.showAgeSelect ? '' : '1',
        Validators.required
      ),
      employer: new FormControl('', Validators.required),
      currentlyWorking: new FormControl(false),
      startMonth: new FormControl('', Validators.required),
      startYear: new FormControl('', [
        Validators.required,
        Validators.min(1950),
        Validators.max(this.currentYear),
      ]),
      endMonth: new FormControl('', Validators.required),
      endYear: new FormControl('', [
        Validators.required,
        Validators.min(1950),
        Validators.max(this.currentYear),
      ]),
      tasks: new FormGroup({}),
      extraActivities: new FormArray([]),
    });

    this.form.get('clusterNumber')?.valueChanges.subscribe(() => {
      this.rebuildTasks();

      // Reset employer if no longer valid for the new cluster
      const currentEmployer = this.form.get('employer')?.value;
      if (currentEmployer && !this.employerOptions.includes(currentEmployer)) {
        this.form.get('employer')?.setValue('', { emitEvent: false });
      }
    });

    this.form.get('currentlyWorking')?.valueChanges.subscribe((val) => {
      const em = this.form.get('endMonth');
      const ey = this.form.get('endYear');
      if (val) {
        em?.clearValidators();
        em?.setValue('');
        ey?.clearValidators();
        ey?.setValue('');
      } else {
        em?.setValidators([Validators.required]);
        ey?.setValidators([
          Validators.required,
          Validators.min(1950),
          Validators.max(this.currentYear),
        ]);
      }
      em?.updateValueAndValidity();
      ey?.updateValueAndValidity();
    });

    if (!this.showAgeSelect) this.rebuildTasks();
  }

  private rebuildTasks(selected: string[] = []): void {
    const keys = this.availableTasks;
    const group: Record<string, FormControl> = {};
    keys.forEach((k) => (group[k] = new FormControl(selected.includes(k))));
    const fg = new FormGroup(group);
    fg.get('other')?.valueChanges.subscribe((v) => {
      if (!v) (this.form.get('extraActivities') as FormArray).clear();
    });
    this.form.setControl('tasks', fg);
    (this.form.get('extraActivities') as FormArray).clear();
  }

  // ── Modal ─────────────────────────────────────────────────────────────────

  openAddModal(): void {
    this.editMode = false;
    this.editingEntry = null;
    this.initForm();
    this.showModal = true;
  }

  openEditModal(entry: FlatEntry, clusterIndex: number): void {
    this.editMode = true;
    this.editingEntry = { ...entry, clusterIndex };
    this.initForm();

    const clusterNum = entry.clusterKey.replace('cluster', '');
    this.form.get('clusterNumber')?.setValue(clusterNum, { emitEvent: false });
    this.form.get('clusterNumber')?.disable();
    this.rebuildTasks(entry.tasks);
    this.form.get('employer')?.setValue(entry.employer);

    if (entry.startMonth) {
      const [sy, sm] = entry.startMonth.split('-');
      this.form.get('startYear')?.setValue(sy);
      this.form.get('startMonth')?.setValue(String(+sm));
    }

    if (entry.endMonth) {
      const [ey, em] = entry.endMonth.split('-');
      this.form.get('endYear')?.setValue(ey);
      this.form.get('endMonth')?.setValue(String(+em));
    } else {
      this.form.get('currentlyWorking')?.setValue(true, { emitEvent: false });
      this.form.get('endMonth')?.clearValidators();
      this.form.get('endMonth')?.updateValueAndValidity();
      this.form.get('endYear')?.clearValidators();
      this.form.get('endYear')?.updateValueAndValidity();
    }

    if (entry.extraTasks?.length) {
      this.tasksGroup.get('other')?.setValue(true, { emitEvent: false });
      entry.extraTasks.forEach((t) =>
        (this.form.get('extraActivities') as FormArray).push(
          new FormControl(t, Validators.required)
        )
      );
    }

    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
    this.editingEntry = null;
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  onDelete(entry: FlatEntry, clusterIndex: number): void {
    const updated: Experiences = {
      cluster1: [...(this.experiences?.cluster1 ?? [])],
      cluster2: [...(this.experiences?.cluster2 ?? [])],
      cluster3: [...(this.experiences?.cluster3 ?? [])],
      cluster4: [...(this.experiences?.cluster4 ?? [])],
    };
    updated[entry.clusterKey].splice(clusterIndex, 1);
    this.deleteExperienceItem.emit({
      clusterKey: entry.clusterKey,
      clusterIndex,
    });
  }

  onSubmit(): void {
    if (this.form.invalid || this.dateError) {
      this.form.markAllAsTouched();
      return;
    }

    const clusterKey = `cluster${
      this.form.get('clusterNumber')?.value
    }` as ClusterKey;
    const sm = +this.form.get('startMonth')!.value;
    const sy = +this.form.get('startYear')!.value;
    const currently = this.isCurrently;
    const em = currently ? null : +this.form.get('endMonth')!.value;
    const ey = currently ? null : +this.form.get('endYear')!.value;

    const tasksVal = this.tasksGroup.value as Record<string, boolean>;
    const tasks = Object.keys(tasksVal).filter(
      (k) => k !== 'other' && tasksVal[k]
    );
    const extraTasks = tasksVal['other']
      ? this.extraActivities.controls
          .map((c) => c.value?.trim())
          .filter(Boolean)
      : [];

    const entry: ExperienceEntry = {
      age: ey && sy ? ey - sy : 0,
      employer: this.form.get('employer')!.value,
      startMonth: `${sy}-${String(sm).padStart(2, '0')}`,
      endMonth: ey && em ? `${ey}-${String(em).padStart(2, '0')}` : null,
      tasks,
      extraTasks,
    };

    const updated: Experiences = {
      cluster1: [...(this.experiences?.cluster1 ?? [])],
      cluster2: [...(this.experiences?.cluster2 ?? [])],
      cluster3: [...(this.experiences?.cluster3 ?? [])],
      cluster4: [...(this.experiences?.cluster4 ?? [])],
    };

    if (this.editMode && this.editingEntry) {
      updated[this.editingEntry.clusterKey].splice(
        this.editingEntry.clusterIndex,
        1
      );
      updated[clusterKey].push(entry);
    } else {
      updated[clusterKey].push(entry);
    }

    this.saveExperienceItem.emit({ experiences: updated });
    this.closeModal();
  }

  // ── Extra activities helpers ──────────────────────────────────────────────

  addExtra(): void {
    if (this.extraActivities.length < this.maxExtra)
      this.extraActivities.push(new FormControl('', Validators.required));
  }

  removeExtra(i: number): void {
    this.extraActivities.removeAt(i);
  }

  extraCtrl(i: number): FormControl {
    return this.extraActivities.at(i) as FormControl;
  }

  // ── Display helpers ───────────────────────────────────────────────────────

  formatMonth(ym: string | null): string {
    if (!ym) return 'In corso';
    const [year, month] = ym.split('-');
    return new Date(+year, +month - 1).toLocaleDateString('it-IT', {
      month: 'long',
      year: 'numeric',
    });
  }

  formatAge(age: string): string {
    const labels = this.getAgeRangeLabels();
    const index = Object.values(CLUSTER_META).findIndex(
      (m) => m.ageRange === age
    );
    return index !== -1 ? labels[index] : age;
  }

  private getAgeRangeLabels(): string[] {
    if (this.type === 'babysitter')
      return [
        'Bambini di età 0-6 mesi',
        'Bambini di età 7-24 mesi',
        'Bambini di età 2-5 anni',
        'Bambini di età 5+ anni',
      ];
    if (this.type === 'petsitter')
      return ['Cani Cuccioli', 'Cani Adulti', 'Cani Anziani'];
    return [];
  }

  taskLabel(id: string): string {
    return TASK_LABELS[id] ?? id;
  }

  trackByIndex(i: number): number {
    return i;
  }
}
